import { useRef, useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import { Play, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react'
import { T } from '../theme'
import useVideoKeyboard from '../hooks/useVideoKeyboard'
import { makeThrottle } from '../utils/throttle'

const D     = '#1a1916'
const GOLD  = '#4F8EF7'
const CREAM = '#f5f2eb'
const MUTED = 'rgba(245,242,235,0.5)'
const RULE  = 'rgba(245,242,235,0.12)'
const MONO  = T.mono

const SPEEDS = [0.25, 0.5, 1, 1.5, 2]

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  if (h > 0) {
    const hh = String(h).padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  }
  return `${mm}:${ss}`
}

// Parse "ss" | "mm:ss" | "hh:mm:ss" -> secondes. Retourne null si invalide.
function parseTime(str) {
  if (!str) return null
  const parts = String(str).trim().split(':').map(p => p.trim())
  if (parts.length === 0 || parts.length > 3) return null
  if (parts.some(p => p === '' || !/^\d+$/.test(p))) return null
  let h = 0, m = 0, s = 0
  if (parts.length === 3)      [h, m, s] = parts.map(Number)
  else if (parts.length === 2) [m, s]    = parts.map(Number)
  else                         [s]       = parts.map(Number)
  const total = h * 3600 + m * 60 + s
  if (!Number.isFinite(total) || total < 0) return null
  return total
}

/**
 * Player video Insightball.
 *
 * Expose via ref :
 *   - getCurrentTime() → number (secondes)
 *   - getDuration() → number (secondes)
 *   - seekTo(seconds)
 *   - pause()
 *   - play()
 *   - captureFrame() → string|null (data:image/jpeg de la frame courante, ou null si CORS bloque)
 *
 * @prop {string}   src                URL S3 signee de la video
 * @prop {boolean}  keyboardEnabled    active les raccourcis (default true)
 * @prop {function} onTimeUpdate       (currentTime) => void — appele a chaque frame
 * @prop {function} onDurationChange   (duration) => void — appele quand la duree est connue
 * @prop {function} onEnded            () => void — fin de lecture du fichier courant
 */
const VideoPlayer = forwardRef(function VideoPlayer({
  src,
  keyboardEnabled = true,
  onTimeUpdate,
  onDurationChange,
  onEnded,
  hideControls = false,
  children,
}, ref) {
  const videoRef = useRef(null)
  const [playing, setPlaying]       = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration]     = useState(0)
  const [speed, setSpeed]           = useState(1)
  const [muted, setMuted]           = useState(false)
  const [buffering, setBuffering]   = useState(false)
  const [error, setError]           = useState(null)

  // Reorganisation des controles sur mobile (< 768px) : pile verticale
  // barre de progression / play+temps+son+plein-ecran / saut / vitesse.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [editingTime, setEditingTime] = useState(false)
  const [timeInput, setTimeInput] = useState('')
  const barRef = useRef(null)
  const draggingRef = useRef(false)
  const wasPlayingRef = useRef(false)
  const seekThrottle = useRef(makeThrottle(30)).current

  // ── Geometrie de l'image reellement affichee ──────────────────────────
  // La boite de <video> n'est PAS l'image : avec width:100% et maxHeight:70vh,
  // une fenetre large et basse donne une boite plus large que ce que le rapport
  // de forme demande, et le navigateur centre l'image avec des bandes (cadrage
  // impose par la spec HTML, independamment d'object-fit).
  // Toute couche posee sur la boite serait donc decalee. On mesure l'image et
  // on y accroche un unique conteneur, partage par le mode annotation et le
  // mode lecture : une seule reference, pas deux qui se calent par coincidence.
  const [intrinsic, setIntrinsic] = useState({ w: 0, h: 0 })
  const [box, setBox]             = useState({ w: 0, h: 0 })

  useVideoKeyboard(videoRef, keyboardEnabled)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // API exposee au parent
  useImperativeHandle(ref, () => ({
    getCurrentTime: () => videoRef.current?.currentTime ?? 0,
    getDuration:    () => videoRef.current?.duration ?? 0,
    seekTo: (seconds) => {
      const v = videoRef.current
      if (!v) return
      v.currentTime = Math.max(0, Math.min(seconds, v.duration || seconds))
    },
    pause: () => videoRef.current?.pause(),
    play:  () => videoRef.current?.play().catch(() => {}),
    /**
     * Capture la frame courante en data:image/jpeg.
     * Retourne null si la video n'est pas chargee ou si CORS bloque (canvas tainted).
     * Pour eviter le tainted canvas, le tag video doit avoir crossOrigin="anonymous"
     * ET le bucket S3 doit retourner les headers CORS (Access-Control-Allow-Origin).
     */
    captureFrame: () => {
      const v = videoRef.current
      if (!v || !v.videoWidth || !v.videoHeight) return null
      try {
        // On limite la resolution a 1280px de large pour eviter de bourrer
        // le state React avec un base64 de 5+ Mo (4K source -> 4Mo en JPEG90).
        const MAX_WIDTH = 1280
        const ratio = v.videoHeight / v.videoWidth
        const w = Math.min(v.videoWidth, MAX_WIDTH)
        const h = Math.round(w * ratio)

        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.drawImage(v, 0, 0, w, h)
        return canvas.toDataURL('image/jpeg', 0.85)
      } catch (e) {
        // SecurityError = CORS bloque (canvas tainted)
        console.error('captureFrame error:', e)
        return null
      }
    },
  }), [])

  // Sync etat
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const onPlay      = () => setPlaying(true)
    const onPause     = () => setPlaying(false)
    const onTime      = () => {
      const t = v.currentTime
      setCurrentTime(t)
      if (onTimeUpdate) onTimeUpdate(t)
    }
    const onLoaded    = () => {
      const d = v.duration || 0
      setDuration(d)
      if (onDurationChange) onDurationChange(d)
    }
    const onWaiting   = () => setBuffering(true)
    const onCanPlay   = () => setBuffering(false)
    const onError     = () => setError('Impossible de charger la video.')
    // `ended` et non un seuil sur `timeupdate` · celui-ci ne tombe qu'environ
    // 4 fois par seconde et s'arrete parfois avant la duree annoncee. Un
    // enchainement de clips qui rate la fin d'un fichier ne repart jamais.
    const onDone      = () => { if (onEnded) onEnded() }

    v.addEventListener('play', onPlay)
    v.addEventListener('pause', onPause)
    v.addEventListener('timeupdate', onTime)
    v.addEventListener('loadedmetadata', onLoaded)
    v.addEventListener('waiting', onWaiting)
    v.addEventListener('canplay', onCanPlay)
    v.addEventListener('error', onError)
    v.addEventListener('ended', onDone)

    return () => {
      v.removeEventListener('play', onPlay)
      v.removeEventListener('pause', onPause)
      v.removeEventListener('timeupdate', onTime)
      v.removeEventListener('loadedmetadata', onLoaded)
      v.removeEventListener('waiting', onWaiting)
      v.removeEventListener('canplay', onCanPlay)
      v.removeEventListener('error', onError)
      v.removeEventListener('ended', onDone)
    }
  }, [src, onTimeUpdate, onDurationChange, onEnded])

  // Mesure de l'image : dimensions intrinseques + taille de la boite.
  // Depend de `src` parce que le <video> est remonte a chaque nouvelle URL
  // signee (key={src}) : videoRef.current pointe alors un AUTRE element, et les
  // dimensions intrinseques de la source precedente seraient perimees. L'URL S3
  // est rafraichie toutes les 50 min, ce n'est pas un cas theorique.
  useEffect(() => {
    const v = videoRef.current
    // Remise a zero avant toute chose : tant que les nouvelles metadonnees ne
    // sont pas la, le repli sur la boite entiere s'applique.
    setIntrinsic({ w: 0, h: 0 })
    if (!v) return

    const readIntrinsic = () => {
      if (v.videoWidth && v.videoHeight) setIntrinsic({ w: v.videoWidth, h: v.videoHeight })
    }
    const readBox = () => setBox({ w: v.clientWidth, h: v.clientHeight })

    // Les metadonnees peuvent etre deja la (source en cache) : l'evenement ne
    // repartirait pas.
    readIntrinsic()
    readBox()
    v.addEventListener('loadedmetadata', readIntrinsic)

    // Un ResizeObserver couvre d'un coup le redimensionnement de fenetre, le
    // basculement mobile/desktop et l'apparition ou la disparition de la barre
    // de controles, sans empiler trois ecouteurs.
    const ro = new ResizeObserver(readBox)
    ro.observe(v)

    return () => {
      v.removeEventListener('loadedmetadata', readIntrinsic)
      ro.disconnect()   // un observer orphelin ne se voit nulle part
    }
  }, [src])

  // Rectangle occupe par l'image dans la boite du <video>, en px relatifs a
  // celle-ci. Repli sur la boite entiere tant qu'on ne sait pas mesurer :
  // c'est exactement le comportement d'avant cette correction.
  const stageRect = useMemo(() => {
    if (!box.w || !box.h || !intrinsic.w || !intrinsic.h) return null
    const scale = Math.min(box.w / intrinsic.w, box.h / intrinsic.h)
    const w = intrinsic.w * scale
    const h = intrinsic.h * scale
    return { left: (box.w - w) / 2, top: (box.h - h) / 2, width: w, height: h }
  }, [box, intrinsic])

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed
  }, [speed, src])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) v.play().catch(() => {})
    else v.pause()
  }

  const toggleMute = () => {
    const v = videoRef.current
    if (!v) return
    v.muted = !v.muted
    setMuted(v.muted)
  }

  // Seek depuis un clientX. Utilise barRef (la barre visuelle) comme reference
  // de largeur. Sert au clic ET au drag (scrub) de la barre de progression.
  const seekFromClientX = (clientX) => {
    const v = videoRef.current
    if (!v || !duration || !barRef.current) return
    const rect = barRef.current.getBoundingClientRect()
    if (rect.width <= 0) return
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    v.currentTime = pct * duration
  }

  // Scrub live de la barre de progression : pause au debut du drag, seekTo en
  // continu (throttle 30ms), resume au release si la video jouait avant.
  const handlePointerDown = (e) => {
    if (!duration) return
    draggingRef.current = true
    const v = videoRef.current
    wasPlayingRef.current = v ? !v.paused : false
    v?.pause()
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* noop */ }
    seekFromClientX(e.clientX)   // clic = seek immediat
  }
  const handlePointerMove = (e) => {
    if (!draggingRef.current) return
    const x = e.clientX
    seekThrottle(() => seekFromClientX(x))
  }
  const handlePointerUp = (e) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* noop */ }
    seekFromClientX(e.clientX)   // position finale exacte (non throttlee)
    if (wasPlayingRef.current) videoRef.current?.play().catch(() => {})
  }

  const seekRelative = (delta) => {
    const v = videoRef.current
    if (!v || !duration) return
    v.currentTime = Math.min(duration, Math.max(0, v.currentTime + delta))
  }

  const commitTimeInput = () => {
    const parsed = parseTime(timeInput)
    setEditingTime(false)
    const v = videoRef.current
    if (parsed != null && v && duration) {
      v.currentTime = Math.min(duration, Math.max(0, parsed))
    }
  }

  const requestFullscreen = () => {
    const v = videoRef.current
    if (!v) return
    if (v.requestFullscreen) v.requestFullscreen().catch(() => {})
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen()
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div style={{ background: '#000', position: 'relative', width: '100%' }}>

      {/* Scene video : le <video> et, par-dessus, le conteneur cale sur l'image
          reellement affichee. Les couches d'annotation (edition et lecture) y
          sont rendues en enfants, donc sur la meme reference par construction —
          et non plus en freres du player, ou leur boite dependait de la
          presence de la barre de controles. */}
      <div style={{ position: 'relative', display: 'block' }}>

      <video
        // key={src} : force un remount complet de l'element <video> a
        // chaque nouvelle URL S3 presignee. Evite qu'une reponse mise en
        // cache SANS headers CORS (chargee avant l'application de la
        // policy bucket) ne taint le canvas (captureFrame -> annotation).
        // On NE peut PAS ajouter ?t= a l'URL : la signature SigV4 porte
        // sur toute la query string -> 403 SignatureDoesNotMatch.
        key={src}
        ref={videoRef}
        src={src}
        crossOrigin="anonymous"
        style={{
          width: '100%',
          maxHeight: '70vh',
          display: 'block',
          background: '#000',
        }}
        playsInline
        preload="metadata"
        // Neutralise explicitement quand les controles sont masques (mode
        // decoupe ou annotation). Jusqu'ici ce play/pause n'etait hors d'atteinte
        // que parce qu'un overlay le recouvrait : un verrou qui ne tient que par
        // empilement de z-index n'est pas un verrou, et la refonte de la
        // geometrie mobile le rouvrirait sans que rien ne le signale.
        onClick={hideControls ? undefined : togglePlay}
      />

      {children && (
        <div
          style={stageRect
            ? {
                position: 'absolute',
                left: stageRect.left, top: stageRect.top,
                width: stageRect.width, height: stageRect.height,
              }
            : { position: 'absolute', inset: 0 }}
        >
          {children}
        </div>
      )}

      </div>

      {buffering && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', pointerEvents: 'none',
          fontFamily: MONO, fontSize: 10, letterSpacing: '.15em',
          textTransform: 'uppercase', color: GOLD,
          background: 'rgba(0,0,0,0.6)', padding: '8px 16px',
        }}>
          Chargement...
        </div>
      )}

      {error && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.85)',
          fontFamily: MONO, fontSize: 11, color: '#ef4444',
          textTransform: 'uppercase', letterSpacing: '.1em',
        }}>
          {error}
        </div>
      )}

      {!hideControls && (
      <div style={{
        background: D,
        borderTop: `1px solid ${RULE}`,
        padding: isMobile ? '12px 12px 14px' : '12px 16px',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>

        {isMobile ? (
          <>
            {/* Ligne 1 — barre de progression cliquable + scrub (hit area 44px) */}
            <ProgressBar
              progress={progress}
              barRef={barRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              isMobile
            />

            {/* Ligne 2 — play / temps / son / plein ecran */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Lecture'}
                style={{
                  width: 44, height: 44, border: 'none', cursor: 'pointer',
                  background: GOLD, color: D, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {playing ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <div style={{
                flex: 1, minWidth: 0, textAlign: 'center',
                fontFamily: MONO, fontSize: 13, color: CREAM,
                letterSpacing: '.05em',
              }}>
                {formatTime(currentTime)}
                <span style={{ color: MUTED }}> / {formatTime(duration)}</span>
              </div>
              <button
                onClick={toggleMute}
                aria-label={muted ? 'Activer le son' : 'Couper le son'}
                style={{
                  width: 44, height: 44, border: `1px solid ${RULE}`,
                  background: 'transparent', color: MUTED, cursor: 'pointer',
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <button
                onClick={requestFullscreen}
                aria-label="Plein ecran"
                style={{
                  width: 44, height: 44, border: `1px solid ${RULE}`,
                  background: 'transparent', color: MUTED, cursor: 'pointer',
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Maximize2 size={16} />
              </button>
            </div>

            {/* Ligne 3 — saut rapide -60 / -10 / temps editable / +10 / +60 */}
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
              <JumpBtn onClick={() => seekRelative(-60)}>-60s</JumpBtn>
              <JumpBtn onClick={() => seekRelative(-10)}>-10s</JumpBtn>
              <input
                type="text"
                inputMode="numeric"
                value={editingTime ? timeInput : formatTime(currentTime)}
                onFocus={() => { setEditingTime(true); setTimeInput(formatTime(currentTime)) }}
                onChange={(e) => setTimeInput(e.target.value)}
                onBlur={commitTimeInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitTimeInput(); e.currentTarget.blur() }
                }}
                aria-label="Position de lecture (mm:ss)"
                style={{
                  flex: 1, minWidth: 0, minHeight: 44,
                  textAlign: 'center',
                  background: D, color: GOLD,
                  border: `1px solid ${GOLD}`,
                  fontFamily: MONO, fontSize: 14, fontWeight: 700,
                  letterSpacing: '.08em',
                  padding: '0 4px', outline: 'none',
                }}
              />
              <JumpBtn onClick={() => seekRelative(10)}>+10s</JumpBtn>
              <JumpBtn onClick={() => seekRelative(60)}>+60s</JumpBtn>
            </div>

            {/* Ligne 4 — vitesse */}
            <div style={{ display: 'flex', gap: 6 }}>
              {SPEEDS.map(s => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  style={{
                    flex: 1, minHeight: 44,
                    fontFamily: MONO, fontSize: 12,
                    letterSpacing: '.04em', cursor: 'pointer',
                    background: s === speed ? GOLD : 'transparent',
                    color: s === speed ? D : MUTED,
                    border: `1px solid ${s === speed ? GOLD : RULE}`,
                    fontWeight: s === speed ? 700 : 400,
                    transition: 'all .15s',
                  }}
                >
                  {s}x
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <ProgressBar
              progress={progress}
              barRef={barRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              isMobile={false}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>

              <button
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Lecture'}
                style={{
                  width: 40, height: 40, border: 'none', cursor: 'pointer',
                  background: GOLD, color: D,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </button>

              <div style={{
                fontFamily: MONO, fontSize: 11, color: CREAM,
                letterSpacing: '.05em', minWidth: 100,
              }}>
                {formatTime(currentTime)}
                <span style={{ color: MUTED }}> / {formatTime(duration)}</span>
              </div>

              <div style={{ flex: 1 }} />

              <div style={{ display: 'flex', gap: 4 }}>
                {SPEEDS.map(s => (
                  <button
                    key={s}
                    onClick={() => setSpeed(s)}
                    style={{
                      padding: '6px 10px', fontFamily: MONO, fontSize: 10,
                      letterSpacing: '.05em', cursor: 'pointer',
                      background: s === speed ? GOLD : 'transparent',
                      color: s === speed ? D : MUTED,
                      border: `1px solid ${s === speed ? GOLD : RULE}`,
                      fontWeight: s === speed ? 700 : 400,
                      transition: 'all .15s',
                    }}
                  >
                    {s}x
                  </button>
                ))}
              </div>

              <button
                onClick={toggleMute}
                aria-label={muted ? 'Activer le son' : 'Couper le son'}
                style={{
                  width: 36, height: 36, border: `1px solid ${RULE}`,
                  background: 'transparent', color: MUTED, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>

              <button
                onClick={requestFullscreen}
                aria-label="Plein ecran"
                style={{
                  width: 36, height: 36, border: `1px solid ${RULE}`,
                  background: 'transparent', color: MUTED, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Maximize2 size={14} />
              </button>
            </div>
          </>
        )}
      </div>
      )}
    </div>
  )
})

// Barre de progression partagee (mobile + desktop) : arrondie, cliquable,
// scrub au drag (pointer events), grossit au hover sur desktop, playhead
// visible au hover. La geometrie de seek vient de barRef (barre interne).
function ProgressBar({ progress, barRef, onPointerDown, onPointerMove, onPointerUp, isMobile }) {
  const [hover, setHover] = useState(false)
  const barH = (hover && !isMobile) ? 10 : 6
  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', cursor: 'pointer', touchAction: 'none',
        // padding vertical = hit area confortable (mobile ~44px, desktop ~22px)
        paddingTop: isMobile ? 18 : 8, paddingBottom: isMobile ? 18 : 8,
      }}
    >
      <div ref={barRef} style={{
        position: 'relative', height: barH, borderRadius: 3,
        background: 'rgba(255,255,255,0.2)', transition: 'height .15s ease',
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${progress}%`, background: GOLD, borderRadius: 3,
          transition: 'width .1s linear',
        }} />
        {hover && !isMobile && (
          <div style={{
            position: 'absolute', top: '50%', left: `${progress}%`,
            transform: 'translate(-50%, -50%)',
            width: 12, height: 12, borderRadius: '50%',
            background: GOLD, pointerEvents: 'none',
            boxShadow: '0 0 0 2px rgba(0,0,0,0.35)',
          }} />
        )}
      </div>
    </div>
  )
}

function JumpBtn({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1, minWidth: 0, minHeight: 44,
        background: D, color: CREAM,
        border: `1px solid ${RULE}`,
        fontFamily: MONO, fontSize: 11, fontWeight: 700,
        letterSpacing: '.02em',
        cursor: 'pointer',
        padding: '0 2px',
      }}
    >
      {children}
    </button>
  )
}

export default VideoPlayer
